import { Link } from 'react-router-dom';
import { Notice, Section } from '../components/Section';
import { trackEvent } from '../lib/analytics';
import { overflowBookingUrl } from '../lib/overflow';
import { usePageMeta } from '../lib/usePageMeta';
import { useSettings } from '../lib/useSettings';
import { EVENT, SEO } from '../seo/pages';

/**
 * What used to be the room catalogue. The hotel block was cancelled on
 * 2026-09-15 and this site sells nothing any more; accommodation goes through
 * SecretsOverflow, the resort's own overflow booking site.
 *
 * It keeps the /rooms path on purpose: that URL is in confirmation emails, in
 * the header of every page the family has shared, and on anything already
 * printed. A redirect to a new path would work, but a page that answers the
 * question is better than a bounce.
 */
export function StayPage() {
  usePageMeta(SEO.rooms.title, SEO.rooms.description, { jsonLd: SEO.rooms.jsonLd });
  const { data: s } = useSettings();
  const venue = EVENT.venue.name;
  const bookingUrl = overflowBookingUrl(s?.eventStartDate);
  const link = 'text-pink-300 underline hover:text-pink-200';

  return (
    <Section eyebrow="Somewhere to sleep" title="Where to stay">
      <div className="grid gap-10 lg:grid-cols-[1.2fr_1fr] lg:items-start">
        <div>
          <p className="text-2xl text-ink-50">{venue} is sold out.</p>
          <p className="mt-6 text-lg text-ink-200">
            If you need a room for the night of the celebration, book it through SecretsOverflow. It
            is the resort&apos;s own overflow site and it exists for exactly this: rooms at hotels a
            few minutes away, with live availability and prices.
          </p>
          <p className="mt-4 text-lg text-ink-200">
            The closest is the{' '}
            <strong className="text-ink-50">Quality Inn &amp; Suites Heritage Park</strong> at 2039
            E Irlo Bronson Memorial Hwy, about a third of a mile up the road from the venue, and it
            is first on the list.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              onClick={() => trackEvent('overflow_click')}
            >
              See rooms for that night
            </a>
            <Link to="/rsvp" className="btn-outline">
              RSVP
            </Link>
          </div>
          <p className="mt-4 text-sm text-ink-400">
            That opens SecretsOverflow with the night of the celebration already filled in, so you
            land on real rooms and prices; change the dates there if you are staying longer.
            SecretsOverflow takes the booking and the payment, so this site is not involved and
            never sees your card. Check the hotel&apos;s own cancellation terms before you pay.
          </p>
        </div>

        <div className="flex flex-col gap-6">
          <Notice tone="warning">
            <strong>Booked a room on this site?</strong> That block was at the old hotel and it is
            gone. Every reservation made here is being refunded to the card that paid for it, and
            there is nothing you need to do.{' '}
            {s?.contactEmail ? (
              <>
                Questions:{' '}
                <a href={`mailto:${s.contactEmail}`} className={link}>
                  {s.contactEmail}
                </a>
                .
              </>
            ) : (
              <>
                Questions:{' '}
                <Link to="/contact" className={link}>
                  contact the family
                </Link>
                .
              </>
            )}
          </Notice>
          <div className="border border-white/10 bg-night-900 p-6">
            <p className="text-meta text-sm">Why this changed</p>
            <p className="mt-2 text-ink-200">
              The celebration moved to {venue}, and the rooms the family had held are no longer part
              of it.{' '}
              <Link to="/update" className={link}>
                Read the family&apos;s update
              </Link>
              .
            </p>
          </div>
        </div>
      </div>
    </Section>
  );
}
